import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { LuClock, LuGauge, LuRepeat, LuTag } from 'react-icons/lu';
import { useTranslation } from '../../../../i18n/LanguageContext';
import { VISIT_TYPES } from '../visitSteps';
import HomeEvaluation from './HomeEvaluation';
import SiteEvaluation from './SiteEvaluation';

const CHIP = 'tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-px-3.5 tw-py-2 tw-text-sm';

/**
 * "Visit Değerlendirme": a summary line (type, duration, cycle time, SLA -
 * calculated exactly as before) over the home or the hospital evaluation.
 */
const EvaluationStep = ({ formData, isReadOnly, isAdmin, projectTitle, step, pendingCompletionLabels, onChange, onEvalChange }) => {
  const { t } = useTranslation();
  const label = (key, vars) => t(`visitModal.evaluation.summary.${key}`, vars);

  const missingKeys = useMemo(() => new Set(step.missing.map((item) => item.key)), [step.missing]);
  // Required / missing counts per section letter
  const sections = useMemo(() => step.checks.reduce((acc, item) => {
    const entry = acc[item.section] || { total: 0, missing: 0 };
    entry.total += 1;
    if (!item.ok) entry.missing += 1;
    return { ...acc, [item.section]: entry };
  }, {}), [step.checks]);

  const visitTypeLabel = VISIT_TYPES.includes(formData.visitType)
    ? t(`visitModal.form.visitTypes.${formData.visitType}`)
    : t('visitModal.common.notSpecified');

  const visitDuration = (() => {
    if (!formData.startTime || !formData.endTime) return '-';
    const [sh, sm] = formData.startTime.split(':').map(Number);
    const [eh, em] = formData.endTime.split(':').map(Number);
    const diffMins = (eh * 60 + em) - (sh * 60 + sm);
    if (diffMins <= 0) return '-';
    return (diffMins / 60).toFixed(2);
  })();

  const cycleTime = (() => {
    if (!formData.date || !formData.visitEvaluation?.processEndDate) return null;
    const start = new Date(formData.date);
    const end = new Date(formData.visitEvaluation.processEndDate);
    const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return diff >= 0 ? diff : null;
  })();

  const slaTarget = parseFloat(formData.visitEvaluation?.slaTarget) || null;
  const slaMet = cycleTime !== null && slaTarget !== null ? cycleTime <= slaTarget : null;
  const slaOverdue = cycleTime !== null && slaTarget !== null ? cycleTime > slaTarget : null;

  return (
    <div className="tw-space-y-4">
      <div className="tw-flex tw-flex-wrap tw-gap-2">
        <span className={`${CHIP} tw-bg-slate-500/[0.08] tw-text-slate-600 dark:tw-text-slate-300`}>
          <LuTag aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" />
          {label('visitType')}:
          <strong className="tw-font-semibold tw-text-slate-900 dark:tw-text-white">{visitTypeLabel}</strong>
        </span>
        <span className={`${CHIP} tw-bg-slate-500/[0.08] tw-text-slate-600 dark:tw-text-slate-300`}>
          <LuClock aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" />
          {label('duration')}:
          <strong className="tw-font-semibold tw-text-slate-900 dark:tw-text-white">
            {visitDuration !== '-' ? label('durationValue', { value: visitDuration }) : '-'}
          </strong>
        </span>
        {cycleTime !== null && (
          <span className={`${CHIP} tw-bg-violet-500/[0.08] tw-text-violet-700 dark:tw-text-violet-300`}>
            <LuRepeat aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('cycleTime')}:
            <strong className="tw-font-semibold">{t('visitModal.common.days', { count: cycleTime })}</strong>
          </span>
        )}
        {slaMet !== null && (
          <span className={`${CHIP} ${slaMet ? 'tw-bg-emerald-500/10 tw-text-emerald-700 dark:tw-text-emerald-300' : 'tw-bg-rose-500/10 tw-text-rose-700 dark:tw-text-rose-300'}`}>
            <LuGauge aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('sla')}:
            <strong className="tw-font-semibold">{slaMet ? label('slaMet') : label('slaOverdue')}</strong>
          </span>
        )}
      </div>

      {formData.visitType === 'home' ? (
        <HomeEvaluation
          formData={formData}
          isReadOnly={isReadOnly}
          isAdmin={isAdmin}
          missingKeys={missingKeys}
          sections={sections}
          onChange={onChange}
          onEvalChange={onEvalChange}
        />
      ) : (
        <SiteEvaluation
          formData={formData}
          isReadOnly={isReadOnly}
          projectTitle={projectTitle}
          missingKeys={missingKeys}
          sections={sections}
          cycleTime={cycleTime}
          slaMet={slaMet}
          slaOverdue={slaOverdue}
          pendingCompletionLabels={pendingCompletionLabels}
          onEvalChange={onEvalChange}
        />
      )}
    </div>
  );
};

EvaluationStep.propTypes = {
  formData: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool,
  isAdmin: PropTypes.bool,
  projectTitle: PropTypes.string,
  step: PropTypes.shape({
    checks: PropTypes.array.isRequired,
    missing: PropTypes.array.isRequired,
  }).isRequired,
  pendingCompletionLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  onEvalChange: PropTypes.func.isRequired,
};

export default EvaluationStep;
